import { NOTIFICATIONS } from "../../constants/routes";
import { useAnnouncementList } from "../../hooks/api/v2/announcement/useAnnouncementList";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import {
  Button,
  DataButton,
  imageUrls,
  InputText,
} from "@sellar-io/components";
import { Icon, SellarIconGradient } from "@sellar-io/components";
import { NotificationList } from "../notifications/NotificationList";
import { Popover } from "../popover/Popover";
import { PopoverContent } from "../popover/PopoverContent";
import { PopoverTrigger } from "../popover/PopoverTrigger";
import { ProfilePicture } from "../profilePicture/ProfilePicture";
import { useMenuDrawerContext } from "../menu/MenuDrawerContextProvider.shared";
import {
  MenuDrawerListElement,
  MenuDrawerListElementIcon,
  MenuDrawerListElementLabel,
  MenuDrawerListElementSection,
} from "../menu/MenuDrawerListElement";
import { twMerge } from "tailwind-merge";
import {
  SellarContextActionEnum,
  useSellarContext,
  useSellarUser,
} from "../../hooks";
import * as ROUTES from "../../constants/routes";
import { useLocation, useNavigate } from "react-router";
import { useDiscoveryV2SearchStore } from "../../pages/discovery/v2/DiscoveryV2SearchStore";
import { useShallow } from "zustand/shallow";
import { Mixpanel } from "../../utils/mixpanel";
import { NewTag } from "../newTag/NewTag";
import { getSellarWebsiteUtms } from "../../utils/utmHelper";
import {
  MenuDisclosure,
  MenuDisclosureButton,
  MenuDisclosurePanel,
} from "../menu/MenuDisclosure";
import {
  SubMenuDrawerListElement,
  SubMenuDrawerListElementIcon,
  SubMenuDrawerListElementLabel,
} from "../menu/SubMenuDrawerListElement";
import { useDraftOrders } from "../../hooks/api/v2/useDraftOrders";
import {
  formatCurrency,
  getSellarCreditLowBalanceInfo,
  pluralize,
} from "@sellar-io/common";
import { useFirebaseUser } from "../../hooks/useFirebaseUser.shared";
import { useIntercom } from "react-use-intercom";
import { useUser } from "../../hooks/api/v2/user/useUser";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useMediaQuery } from "usehooks-ts";
import { IconType } from "@sellar-io/components";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { Link } from "react-router";
import preordersBackgroundFillSVG from "../../images/preorders.svg";
import { SellarCreditCheckDialog } from "../../pages/sellarCredit/components/SellarCreditCheckDialog/SellarCreditCheckDialog";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../modal/Dialog";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { useBusinessUserCreditAccess } from "../../hooks/api/v2/business/useBusinessUserCreditAccess";
import { apiV2AssertError } from "../../hooks/api/v2/apiV2Request";
import { Alert } from "../alert/Alert";
import {
  TooltipDismiss,
  TooltipDismissContent,
} from "../tooltipDismiss/TooltipDismiss";
import { useUserUpdate } from "../../hooks/api/v2/user/useUserUpdate";
import { useNotificationList } from "../../hooks/api/v2/notification/useNotificationList";
import { useLegalEntityCompanyCreditInfo } from "../../hooks/api/v2/legalEntity/useLegalEntityCompanyCreditInfo";
import { CompanyCreditInfoResponseType } from "@sellar-io/api-v2/types";
import { TwoSellarCreditVerificationRequiredAlert } from "../twoSellarCreditVerificationRequiredAlert/TwoSellarCreditVerificationRequiredAlert";
import { useLegalEntityId } from "../../hooks/api/v2/legalEntity/useLegalEntityId";
import { SidebarDraftBaskets } from "./SidebarDraftBaskets";
import { isBusinessOnboardingIncomplete } from "../../utils/isBusinessOnboardingIncomplete";

const SidebarSection = MenuDrawerListElementSection;

const SidebarContext = createContext<{
  setShowSellarCreditBalanceTooltip: (active: boolean) => void;
}>({ setShowSellarCreditBalanceTooltip: () => {} });

export const Sidebar = () => {
  const [creditDialogActive, setCreditDialogActive] = useState(false);
  const { isActive } = useRemoteFeatureFlags();
  const { business } = useSellarCurrentBusiness();
  const { user: sellarUser } = useSellarUser();
  const sellarUserId = sellarUser?.id ?? 0;
  const { orders: draftOrders } = useDraftOrders();
  const { user } = useFirebaseUser();
  const userInDb = useUser(sellarUserId, {
    enabled: Boolean(sellarUserId),
  });
  const userProfileImage = userInDb.data?.profileImageUrl ?? "";
  const isBuyer =
    Boolean(sellarUser) && Boolean(business) && Boolean(business?.Retailer);
  const isSupplier =
    Boolean(sellarUser) && Boolean(business) && Boolean(business?.Supplier);
  const supplierUrlId = business?.Supplier?.urlId ?? "";
  const hasCompletedAccountSetup = Boolean(business);
  const isBuyerAndIsOnboarding =
    isBuyer && isBusinessOnboardingIncomplete(business);
  const hasDraftOrders = draftOrders && draftOrders.length >= 1;
  const isNotOrderOnlyAccount =
    Boolean(business) &&
    isBuyer &&
    business?.BusinessUser?.role !== "orderOnly";
  const canSeeBuyerPreorderBanner =
    isBuyer && isActive("retailerPreorderBanner");
  const canSeeSupplierPromoBanner =
    isSupplier && isActive("supplierPromoBanner");
  const userDisplayName =
    userInDb.data?.name ?? user?.firebaseUser.displayName ?? "";
  const userEmail = user?.firebaseUser.email ?? "";
  const { data: companyCreditInfoData } = useLegalEntityCompanyCreditInfo(
    {
      RetailerId: business?.Retailer?.id ?? 0,
    },
    {
      enabled: Boolean(business?.Retailer?.id) && Boolean(sellarUser?.id),
    },
  );

  const showSellarCreditBanner =
    ["eligible", "creditCheckNotPerformed"].includes(
      companyCreditInfoData?.status ?? "",
    ) && !!business?.Retailer;

  const [showSellarCreditBalanceTooltip, setShowSellarCreditBalanceTooltip] =
    useState(false);

  const sdConsolidatedCheckoutEnabled = isActive("sdConsolidatedCheckout");

  return (
    <SidebarContext.Provider
      value={{
        setShowSellarCreditBalanceTooltip,
      }}
    >
      <div className="flex flex-1 flex-col justify-between">
        <SidebarHeader />
        <div className="flex flex-1 flex-col justify-between">
          <SidebarSection className="flex flex-col p-2">
            {isBuyer && (
              <>
                <SidebarSwitcherContainer
                  className={
                    showSellarCreditBalanceTooltip
                      ? "shadow-[0_0px_8px_0_rgba(181,87,241,0.5)]"
                      : ""
                  }
                  switcher={
                    <BusinessSwitcher
                      companyCreditInfo={companyCreditInfoData ?? null}
                    />
                  }
                />
                <SidebarBuyerRoutes />
              </>
            )}
            {isSupplier && (
              <>
                <SidebarSwitcherContainer
                  switcher={<BusinessSwitcher companyCreditInfo={null} />}
                >
                  <Availability />
                </SidebarSwitcherContainer>
                <SidebarSupplierRoutes supplierUrlId={supplierUrlId} />
              </>
            )}
            {isNotOrderOnlyAccount && <Discover />}
          </SidebarSection>
          <SidebarSection className="flex flex-col p-2">
            {/* We are reusing the pre-order banner flag for sellar credit */}
            {canSeeBuyerPreorderBanner && showSellarCreditBanner && (
              <FeatureBanner
                title="Sellar Credit"
                subtitles={[
                  "Up to 60 day terms",
                  "No fees",
                  "Use with 200+ suppliers",
                ]}
                buttonText="Get Started"
                onClick={() => setCreditDialogActive(true)}
                classes="from-[#B557F1] to-[#D213A6]"
                borderColor="border-[#B557F1]"
              />
            )}
            {canSeeSupplierPromoBanner && (
              <FeatureBanner
                title="Sellar Credit"
                subtitles={[
                  "Win new customers",
                  "Grow basket size",
                  "Offer up to 60 days credit",
                ]}
                buttonText="Learn more"
                linkUrl="https://intercom.help/sellario/en/articles/10600852-supplier-guide-activating-sellar-credit-with-your-customers"
                classes="from-[#B557F1] to-[#D213A6]"
                borderColor="border-[#B557F1]"
              />
            )}
            {!hasCompletedAccountSetup && <CompleteAccountSetup />}
            {isBuyerAndIsOnboarding && <CompleteOnboarding />}
            {isBuyer && sdConsolidatedCheckoutEnabled && (
              <SidebarDraftBaskets />
            )}
            {hasCompletedAccountSetup &&
              hasDraftOrders &&
              !sdConsolidatedCheckoutEnabled && (
                <DraftBasket nbDraftOrders={draftOrders.length} />
              )}
            <GetHelp />
          </SidebarSection>
        </div>
        <SidebarFooter
          user={{
            displayName: userDisplayName,
            email: userEmail,
          }}
          userProfileImage={userProfileImage}
        />
      </div>
      <SellarCreditCheckDialog
        active={creditDialogActive}
        onDismiss={() => setCreditDialogActive(false)}
      />
    </SidebarContext.Provider>
  );
};

const SidebarHeader = () => {
  const {
    isRailDisplayType,
    isMobileDisplayType,
    setDisplayType,
    setIsOnScreen,
  } = useMenuDrawerContext();
  const { isActive } = useRemoteFeatureFlags();
  const inAppNotificationsFF = isActive("inAppNotifications");

  const icon = isRailDisplayType ? (
    <MenuDrawerListElementIcon icon="MENU" />
  ) : (
    <MenuDrawerListElementIcon icon="MENU_OPEN" />
  );

  return (
    <div
      className={twMerge(
        "flex items-center justify-between px-2 py-1.5",
        isRailDisplayType && "pl-2",
      )}
    >
      {!isRailDisplayType && (
        <img
          className="h-6"
          alt="logo"
          src={imageUrls.sellarLogo.purple.full}
        />
      )}
      <div
        className={twMerge(
          "flex flex-row items-center gap-1",
          isRailDisplayType && "flex-col-reverse",
        )}
      >
        {inAppNotificationsFF && <NotificationPopOver />}
        <MenuDrawerListElement
          className="my-0.5 h-10 w-10 justify-center p-0"
          onClick={() => {
            if (isMobileDisplayType) {
              setIsOnScreen(false);
              return;
            }
            setDisplayType(isRailDisplayType ? "sticky" : "rail");
          }}
        >
          {icon}
        </MenuDrawerListElement>
      </div>
    </div>
  );
};

const NotificationPopOver = () => {
  const { business } = useSellarCurrentBusiness();
  const navigate = useNavigate();
  const [filterActive, setFilterActive] = useState(false);
  const { setIsOnScreen } = useMenuDrawerContext();

  // Check for high priority unread notifications
  const highPriorityUnreadQuery = useNotificationList(
    {
      query: [
        { type: "priority", value: "high", modifier: "EQUALS" },
        { type: "unRead", value: true, modifier: "EQUALS" },
      ],
      limit: 1,
    },
    {
      refetchInterval: 10 * 60 * 1000, // Poll every 10 minutes for new notifications
      refetchIntervalInBackground: false, // Don't poll when tab is not active
    },
  );

  // Check for unread announcement
  const unreadAnnouncementQuery = useAnnouncementList(
    {
      query: [{ type: "unRead", value: true, modifier: "EQUALS" }],
      limit: 1,
    },
    {
      enabled: !!business?.Retailer,
      refetchInterval: 10 * 60 * 1000, // Poll every 10 minutes for new notifications
      refetchIntervalInBackground: false, // Don't poll when tab is not active
    },
  );

  const hasUnreadAnnouncement =
    (unreadAnnouncementQuery.data?.pages[0]?.length || 0) > 0;

  const hasHighPriorityUnread =
    (highPriorityUnreadQuery.data?.pages[0]?.length || 0) > 0;

  const showRedDot = hasUnreadAnnouncement || hasHighPriorityUnread;

  const isMobile = useMediaQuery("(max-width: 769px)");
  return (
    <>
      {isMobile ? (
        <Icon
          icon={"BELL_OUTLINE"}
          onClick={() => {
            navigate(NOTIFICATIONS);
            setIsOnScreen(false);
          }}
        />
      ) : (
        <div className="relative">
          <Popover
            trigger="click"
            open={filterActive}
            onOpenChange={setFilterActive}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <DataButton
                onClick={() => setFilterActive(!filterActive)}
                border={false}
                active={filterActive}
                className="my-0.5 h-10 min-h-10 w-10 p-0"
              >
                <Icon icon="BELL_OUTLINE" size={1} />
              </DataButton>
            </PopoverTrigger>
            <PopoverContent className="w-[476px] rounded-lg shadow-md">
              <NotificationList
                setPopoverActive={setFilterActive}
                isNotificationPopover={true}
              >
                <div className="flex w-full items-center justify-center p-2">
                  <Button
                    color={"transparent"}
                    onClick={() => {
                      navigate(NOTIFICATIONS);
                      setFilterActive(false);
                    }}
                  >
                    See all <Icon icon={"CHEVRON_RIGHT"} />
                  </Button>
                </div>
              </NotificationList>
            </PopoverContent>
          </Popover>
          {showRedDot && (
            <div className="absolute top-3 right-3 h-2 w-2 rounded-full bg-red-500" />
          )}
        </div>
      )}
    </>
  );
};

type SidebarSwitcherContainerProps = {
  switcher: ReactNode;
  children?: ReactNode;
  className?: string;
};
const SidebarSwitcherContainer = ({
  switcher,
  className,
  children,
}: SidebarSwitcherContainerProps) => {
  return (
    <div
      className={twMerge(
        "flex flex-col rounded-lg border border-zinc-300",
        className,
      )}
    >
      {switcher}
      {children && (
        <>
          <hr className="border-b-0 border-zinc-300" />
          {children}
        </>
      )}
    </div>
  );
};

const BusinessSwitcher = ({
  companyCreditInfo,
}: {
  companyCreditInfo: CompanyCreditInfoResponseType | null;
}) => {
  const { isRailDisplayType, setDisplayType, setIsOnScreen } =
    useMenuDrawerContext();
  const [creditFlowActive, setCreditFlowActive] = useState(false);
  const [requestCreditActive, setRequestCreditActive] = useState(false);
  const { user } = useSellarUser();
  const { business } = useSellarCurrentBusiness();
  const [, dispatch] = useSellarContext();
  const logoUrl = business ? business.logoUrl : "";
  const businessName = business ? business.name : "";
  const navigate = useNavigate();
  const { setShowSellarCreditBalanceTooltip } = useContext(SidebarContext);
  const location = useLocation();
  const isSellarCreditGetStartedFlow = location.pathname.includes(
    ROUTES.SELLAR_CREDIT_GET_STARTED,
  );

  const updateUserMutation = useUserUpdate(user?.id ?? 0);

  const handleOnClickSwitcher = () => {
    dispatch({
      type: SellarContextActionEnum.CHANGE_BUSINESS_SWITCHER_ACTIVE,
      businessSwitcherActive: true,
    });
  };

  const legalEntityId = useLegalEntityId();
  const creditAmountAvailable = companyCreditInfo?.creditAmountAvailable ?? 0;
  const creditAmountInUse = companyCreditInfo?.creditAmountInUse ?? 0;

  const isUserLegalEntityAdmin = Boolean(user?.legalEntityAdminList[0]);
  const isUserSellarAdmin = Boolean(user?.type === "admin");
  const [userHasViewedCreditBalance, setUserHasViewedCreditBalance] = useState(
    user?.creditBalanceViewed,
  );

  const showSellarCreditBalanceTooltip =
    isUserLegalEntityAdmin &&
    !userHasViewedCreditBalance &&
    !isSellarCreditGetStartedFlow;

  useEffect(() => {
    setShowSellarCreditBalanceTooltip(showSellarCreditBalanceTooltip);
  }, [setShowSellarCreditBalanceTooltip, showSellarCreditBalanceTooltip]);

  if (isRailDisplayType) {
    return (
      <>
        <div className="flex flex-col items-center">
          <div className="flex items-center justify-center py-2">
            <ProfilePicture
              size="small"
              logoUrl={logoUrl}
              onClick={() => setDisplayType("sticky")}
            />
          </div>
          <hr className="border-0 border-t border-zinc-300" />
          <Button
            onClick={handleOnClickSwitcher}
            size="small"
            color="transparent"
          >
            <Icon size={1} icon="SWAP_VERTICAL" color="#27272a" />
          </Button>
        </div>
      </>
    );
  }

  const isSellarCreditBalanceLow = Boolean(
    getSellarCreditLowBalanceInfo({
      creditLimit: companyCreditInfo?.creditLimit ?? 0,
      availableBalance: companyCreditInfo?.creditAmountAvailable ?? 0,
    }).data?.isLow,
  );

  const userCanViewSellarCreditBalance =
    companyCreditInfo?.userCanViewSellarCreditBalance ||
    isUserSellarAdmin ||
    isUserLegalEntityAdmin;

  const userCanAccessSellarCredit =
    companyCreditInfo?.userCanAccessSellarCredit || isUserLegalEntityAdmin;

  const userCanAccessCreditTab =
    (userCanAccessSellarCredit && userCanViewSellarCreditBalance) ||
    isUserSellarAdmin;

  const companyApproved = companyCreditInfo?.status === "approved";

  const companyDeclinedWithOutstandingCredit =
    !!legalEntityId &&
    companyCreditInfo?.status === "declined" &&
    creditAmountInUse > 0;

  const companyDeclinedWithNoOutstandingCredit =
    !!legalEntityId &&
    ["creditCheckNotPerformed", "declined", "noCreditOffered"].includes(
      companyCreditInfo?.status ?? "",
    ) &&
    creditAmountInUse === 0;

  return (
    <>
      <div>
        <div className="flex justify-between">
          <Link
            to={ROUTES.BUSINESS_SETTINGS}
            onClick={() => setIsOnScreen(false)}
            className="flex flex-1 items-center gap-[6px] rounded-l-lg border-r border-zinc-300 p-2 hover:bg-zinc-100"
          >
            <ProfilePicture
              size="small"
              logoUrl={logoUrl}
              name={businessName}
            />
            <div className="text-sm font-medium text-zinc-800 underline">
              <span className="line-clamp-2">{businessName}</span>
            </div>
          </Link>
          <Button
            onClick={handleOnClickSwitcher}
            color="transparent"
            size="small"
            className="flex h-auto border-none hover:rounded-tl-none hover:rounded-bl-none"
          >
            <Icon size={0.9} icon="SWAP_VERTICAL" color="#27272a" />
          </Button>
        </div>
        {/* soft credit check has been performed show quoted credit limit  */}
        {companyCreditInfo?.status === "eligible" && (
          <CreditEligible
            creditLimitQuoteAmount={companyCreditInfo.creditLimitQuote ?? 0}
            onClick={() => setCreditFlowActive(true)}
          />
        )}
        {/* company has credit  */}
        {(companyApproved || companyDeclinedWithOutstandingCredit) && (
          <CreditBalance
            creditAmountAvailable={creditAmountAvailable}
            isSellarCreditBalanceLow={isSellarCreditBalanceLow}
            creditPermissions={{
              canView: userCanViewSellarCreditBalance,
              canAccess: userCanAccessSellarCredit || isUserSellarAdmin,
            }}
            onClickBalance={() => {
              // if has access, navigate to credit tab
              if (userCanAccessCreditTab) {
                return navigate(`${ROUTES.SELLAR_CREDIT_TAB}?provider=two`);
              }

              // if user cannot access sellar credit, show request access dialog

              return setRequestCreditActive(true);
            }}
            onCloseTooltip={() => {
              setUserHasViewedCreditBalance(true);
              updateUserMutation.mutate({
                data: {
                  creditBalanceViewed: true,
                },
              });
            }}
            showSellarCreditBalanceTooltip={showSellarCreditBalanceTooltip}
            noLongerEligibleForSellarCredit={
              companyCreditInfo.status === "declined"
            }
          />
        )}

        {userCanAccessCreditTab &&
          companyDeclinedWithNoOutstandingCredit &&
          companyCreditInfo?.creditProvider === "two" && (
            <TwoCreditUnavilable
              onClick={() => {
                return navigate(ROUTES.SELLAR_CREDIT_CREDIT_PROVIDER_SELECTION);
              }}
            />
          )}
        {/* Two merchant customer user verification alert */}
        {companyCreditInfo?.creditProvider === "two" &&
          companyCreditInfo.status === "approved" &&
          !companyCreditInfo.isCompanyVerified && (
            <div className="w-full p-2">
              <TwoSellarCreditVerificationRequiredAlert
                fontSize="text-xs"
                buttonFontSize="text-sm"
                message="To keep using Sellar Credit, you need to verify your account"
              />
            </div>
          )}
      </div>
      <SellarCreditCheckDialog
        active={creditFlowActive}
        onDismiss={() => setCreditFlowActive(false)}
      />
      <RequestCreditAccessDialog
        active={requestCreditActive}
        company={business?.name ?? ""}
        onDismiss={() => setRequestCreditActive(false)}
      />
    </>
  );
};

const Availability = () => {
  const navigate = useNavigate();
  const { isRailDisplayType, setSubMenuIndex } = useMenuDrawerContext();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const navigateToStorefrontLinks = () => {
    const route = isDesktop
      ? ROUTES.STOREFRONT_LINKS_PREVIEW
      : ROUTES.STOREFRONT_LINKS;
    navigate(route);
    setSubMenuIndex(0);
  };

  if (isRailDisplayType) {
    return (
      <Button
        onClick={navigateToStorefrontLinks}
        size="small"
        className="w-full rounded-t-none"
      >
        <Icon size={1} icon="LINK" color="white" />
      </Button>
    );
  }

  return (
    <div className="flex gap-2 p-2">
      <Button
        onClick={navigateToStorefrontLinks}
        size="small"
        className="flex-1"
      >
        <Icon size={1} icon="LINK" color="white" />
        Share link
      </Button>
    </div>
  );
};

const SidebarBuyerRoutes = () => {
  const navigate = useNavigate();
  return (
    <>
      <MenuDrawerListElement
        route={ROUTES.SUPPLIERS}
        onClick={() => navigate(ROUTES.SUPPLIERS)}
      >
        <MenuDrawerListElementIcon icon="ACCOUNT_SUPERVISOR_OUTLINE" />
        <MenuDrawerListElementLabel>My Suppliers</MenuDrawerListElementLabel>
      </MenuDrawerListElement>
      <MenuDrawerListElement
        route={ROUTES.ORDERS}
        onClick={() => navigate(ROUTES.ORDERS)}
      >
        <MenuDrawerListElementIcon icon="CART_OUTLINE" />
        <MenuDrawerListElementLabel>Orders</MenuDrawerListElementLabel>
      </MenuDrawerListElement>
      <MenuDrawerListElement
        route={ROUTES.DIRECT_MESSAGE_LIST}
        onClick={() => navigate(ROUTES.DIRECT_MESSAGE_LIST)}
      >
        <MenuDrawerListElementIcon icon="MESSAGE_OUTLINE" />
        <MenuDrawerListElementLabel>Messages</MenuDrawerListElementLabel>
      </MenuDrawerListElement>
    </>
  );
};

const Discover = () => {
  const navigate = useNavigate();
  const { reset } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      reset: state.reset,
    })),
  );

  return (
    <MenuDrawerListElement
      route={ROUTES.DISCOVER}
      onClick={() => {
        Mixpanel.track("Discovery: Discover Tab Clicked", {
          ...getSellarWebsiteUtms(),
        });
        //we need to reset the discovery state before navigating
        reset();
        navigate(ROUTES.DISCOVER);
      }}
    >
      <MenuDrawerListElementIcon icon="MAGNIFY" />
      <MenuDrawerListElementLabel>
        Market
        <NewTag className="ml-2" />
      </MenuDrawerListElementLabel>
    </MenuDrawerListElement>
  );
};

const SidebarSupplierRoutes = ({
  supplierUrlId,
}: {
  supplierUrlId: string;
}) => {
  const navigate = useNavigate();

  const unbundlingRelease2aEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingRelease2A",
  );

  const unbundlingRelease2bEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingRelease2B",
  );

  const unbundlingEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingReleaseFinal",
  );

  const sellarDistributionEnabled = useRemoteFeatureFlags().isActive(
    "sellarDistributionBeta",
  );

  const supplierOrderPromotionsEnabled = useRemoteFeatureFlags().isActive(
    "supplierOrderPromotions",
  );

  return (
    <>
      <MenuDisclosure
        index={1}
        routes={[ROUTES.CUSTOMERS, ROUTES.DIRECT_MESSAGE_LIST, ROUTES.BUYERS]}
      >
        <MenuDisclosureButton onClick={() => navigate(ROUTES.CUSTOMERS)}>
          <MenuDrawerListElementIcon icon="ACCOUNT_MULTIPLE_OUTLINE" />
          <MenuDrawerListElementLabel>Customers</MenuDrawerListElementLabel>
        </MenuDisclosureButton>
        <MenuDisclosurePanel>
          <SubMenuDrawerListElement
            route={ROUTES.CUSTOMERS}
            onClick={() => navigate(ROUTES.CUSTOMERS)}
          >
            <SubMenuDrawerListElementIcon icon="FORMAT_LIST_BULLETED" />
            <SubMenuDrawerListElementLabel>List</SubMenuDrawerListElementLabel>
          </SubMenuDrawerListElement>
          {!unbundlingEnabled && (
            <SubMenuDrawerListElement
              route={ROUTES.CUSTOMER_ZONES}
              onClick={() => navigate(ROUTES.CUSTOMER_ZONES)}
            >
              <SubMenuDrawerListElementIcon icon="MAP_MARKER_OUTLINE" />
              <SubMenuDrawerListElementLabel>
                Customer Zones
              </SubMenuDrawerListElementLabel>
            </SubMenuDrawerListElement>
          )}
          <SubMenuDrawerListElement
            route={ROUTES.DIRECT_MESSAGE_LIST}
            onClick={() => navigate(ROUTES.DIRECT_MESSAGE_LIST)}
          >
            <SubMenuDrawerListElementIcon icon="MESSAGE_OUTLINE" />
            <SubMenuDrawerListElementLabel>
              Messages
            </SubMenuDrawerListElementLabel>
          </SubMenuDrawerListElement>
          <SubMenuDrawerListElement
            route={ROUTES.BUYERS}
            onClick={() => navigate(ROUTES.BUYERS)}
          >
            <SubMenuDrawerListElementIcon icon="PLUS" />
            <SubMenuDrawerListElementLabel>
              Add New
            </SubMenuDrawerListElementLabel>
          </SubMenuDrawerListElement>
        </MenuDisclosurePanel>
      </MenuDisclosure>

      <MenuDisclosure index={2} routes={[ROUTES.ORDERS, ROUTES.ANALYTICS]}>
        <MenuDisclosureButton onClick={() => navigate(ROUTES.ORDERS)}>
          <MenuDrawerListElementIcon icon="CART_OUTLINE" />
          <MenuDrawerListElementLabel>Sales</MenuDrawerListElementLabel>
        </MenuDisclosureButton>
        <MenuDisclosurePanel>
          <SubMenuDrawerListElement
            route={ROUTES.ORDERS}
            onClick={() => navigate(ROUTES.ORDERS)}
          >
            <SubMenuDrawerListElementIcon icon="INBOX_ARROW_DOWN_OUTLINE" />
            <SubMenuDrawerListElementLabel>
              Orders
            </SubMenuDrawerListElementLabel>
          </SubMenuDrawerListElement>

          <SubMenuDrawerListElement
            route={ROUTES.ANALYTICS}
            onClick={() => navigate(ROUTES.ANALYTICS)}
          >
            <SubMenuDrawerListElementIcon icon="CHART_BOX_OUTLINE" />
            <SubMenuDrawerListElementLabel>
              Analytics
            </SubMenuDrawerListElementLabel>
          </SubMenuDrawerListElement>
        </MenuDisclosurePanel>
      </MenuDisclosure>

      <MenuDisclosure
        index={3}
        routes={[
          ROUTES.COLLECTIONS,
          ROUTES.DISTRIBUTION,
          ROUTES.DISTRIBUTION_PALLET_SHIPMENTS,
        ]}
      >
        <MenuDisclosureButton onClick={() => navigate(ROUTES.COLLECTIONS)}>
          <MenuDrawerListElementIcon icon="PACKAGE_VARIANT_CLOSED_PLUS" />
          <MenuDrawerListElementLabel>Dispatch</MenuDrawerListElementLabel>
        </MenuDisclosureButton>
        <MenuDisclosurePanel>
          <SubMenuDrawerListElement
            route={ROUTES.COLLECTIONS}
            onClick={() => navigate(ROUTES.COLLECTIONS)}
          >
            <SubMenuDrawerListElementIcon icon="PACKAGE_VARIANT_CLOSED" />
            <SubMenuDrawerListElementLabel>
              Courier Collections
            </SubMenuDrawerListElementLabel>
          </SubMenuDrawerListElement>
          {sellarDistributionEnabled && (
            <>
              <SubMenuDrawerListElement
                route={ROUTES.DISTRIBUTION}
                onClick={() => navigate(`${ROUTES.DISTRIBUTION}?tab=Allocated`)}
              >
                <SubMenuDrawerListElementIcon icon="HAND_EXTENDED_OUTLINE" />
                <SubMenuDrawerListElementLabel>
                  SD Collections
                </SubMenuDrawerListElementLabel>
              </SubMenuDrawerListElement>

              <SubMenuDrawerListElement
                route={ROUTES.DISTRIBUTION_PALLET_SHIPMENTS}
                onClick={() =>
                  navigate(`${ROUTES.DISTRIBUTION_PALLET_SHIPMENTS}`)
                }
              >
                <SubMenuDrawerListElementIcon icon="PALLET" />
                <SubMenuDrawerListElementLabel>
                  SD Pallet Shipments
                </SubMenuDrawerListElementLabel>
              </SubMenuDrawerListElement>
            </>
          )}
        </MenuDisclosurePanel>
      </MenuDisclosure>

      <MenuDisclosure
        index={4}
        routes={[
          ROUTES.PRODUCT_LIST,
          ROUTES.PRICE_TIERS,
          ROUTES.STOCK_OVERVIEW,
          ROUTES.PROMOTIONS_STANDALONE,
        ]}
      >
        <MenuDisclosureButton onClick={() => navigate(ROUTES.PRODUCT_LIST)}>
          <MenuDrawerListElementIcon icon="FACTORY_OUTLINE" />
          <MenuDrawerListElementLabel>Inventory</MenuDrawerListElementLabel>
        </MenuDisclosureButton>
        <MenuDisclosurePanel>
          <SubMenuDrawerListElement
            route={ROUTES.PRODUCT_LIST}
            onClick={() => navigate(ROUTES.PRODUCT_LIST)}
          >
            <SubMenuDrawerListElementIcon icon="FORMAT_LIST_BULLETED" />
            <SubMenuDrawerListElementLabel>
              Product List
            </SubMenuDrawerListElementLabel>
          </SubMenuDrawerListElement>
          <SubMenuDrawerListElement
            route={ROUTES.STOCK_OVERVIEW}
            onClick={() => navigate(ROUTES.STOCK_OVERVIEW)}
          >
            <SubMenuDrawerListElementIcon icon="CLIPBOARD_CHECK_OUTLINE" />
            <SubMenuDrawerListElementLabel>
              Stock Overview
            </SubMenuDrawerListElementLabel>
          </SubMenuDrawerListElement>
          {!unbundlingRelease2bEnabled && (
            <SubMenuDrawerListElement
              route={ROUTES.PRICE_TIERS}
              onClick={() => navigate(ROUTES.PRICE_TIERS)}
            >
              <SubMenuDrawerListElementIcon icon="TAG_OUTLINE" />
              <SubMenuDrawerListElementLabel>
                Price Tiers
              </SubMenuDrawerListElementLabel>
            </SubMenuDrawerListElement>
          )}

          {!unbundlingRelease2bEnabled && supplierOrderPromotionsEnabled && (
            <SubMenuDrawerListElement
              route={ROUTES.PROMOTIONS_STANDALONE}
              onClick={() => navigate(ROUTES.PROMOTIONS_STANDALONE)}
            >
              <SubMenuDrawerListElementIcon icon="PARTY_POPPER" />
              <SubMenuDrawerListElementLabel>
                Promotions
              </SubMenuDrawerListElementLabel>
            </SubMenuDrawerListElement>
          )}

          {unbundlingRelease2bEnabled && (
            <SubMenuDrawerListElement
              route={ROUTES.PRICE_LISTS}
              onClick={() => navigate(ROUTES.PRICE_LISTS)}
            >
              <SubMenuDrawerListElementIcon icon="TAG_OUTLINE" />
              <SubMenuDrawerListElementLabel>
                Pricing
              </SubMenuDrawerListElementLabel>
            </SubMenuDrawerListElement>
          )}

          <SubMenuDrawerListElement
            route={ROUTES.AVAILABILITY_TIERS}
            onClick={() => navigate(ROUTES.AVAILABILITY_TIERS)}
          >
            <SubMenuDrawerListElementIcon icon="BOOK_OUTLINE" />
            <SubMenuDrawerListElementLabel>
              Product Catalogues
            </SubMenuDrawerListElementLabel>
          </SubMenuDrawerListElement>
        </MenuDisclosurePanel>
      </MenuDisclosure>

      {unbundlingEnabled ? (
        <MenuDrawerListElement
          onClick={() => navigate(ROUTES.DELIVERY_METHODS)}
          route={ROUTES.DELIVERY_METHODS}
        >
          <MenuDrawerListElementIcon icon="TRUCK_OUTLINE" />
          <MenuDrawerListElementLabel>Delivery</MenuDrawerListElementLabel>
        </MenuDrawerListElement>
      ) : (
        <MenuDisclosure
          index={5}
          routes={[
            ROUTES.DELIVERY_SETTINGS,
            ROUTES.DELIVERY_CHARGES,
            ROUTES.SUPPLIER_DISTRIBUTION,
          ]}
        >
          <MenuDisclosureButton
            onClick={() => navigate(ROUTES.DELIVERY_SETTINGS)}
          >
            <MenuDrawerListElementIcon icon="TRUCK_OUTLINE" />
            <MenuDrawerListElementLabel>Delivery</MenuDrawerListElementLabel>
          </MenuDisclosureButton>
          <MenuDisclosurePanel>
            <SubMenuDrawerListElement
              route={ROUTES.SUPPLIER_DISTRIBUTION}
              onClick={() => navigate(ROUTES.SUPPLIER_DISTRIBUTION)}
            >
              <SubMenuDrawerListElementIcon icon="TRUCK_OUTLINE" />
              <SubMenuDrawerListElementLabel>
                Sellar Distribution
              </SubMenuDrawerListElementLabel>
            </SubMenuDrawerListElement>
            <SubMenuDrawerListElement
              route={ROUTES.DELIVERY_SETTINGS}
              onClick={() => navigate(ROUTES.DELIVERY_SETTINGS)}
            >
              <SubMenuDrawerListElementIcon icon="COG_OUTLINE" />
              <SubMenuDrawerListElementLabel>
                Settings
              </SubMenuDrawerListElementLabel>
            </SubMenuDrawerListElement>

            <SubMenuDrawerListElement
              route={ROUTES.DELIVERY_CHARGES}
              onClick={() => navigate(ROUTES.DELIVERY_CHARGES)}
            >
              <SubMenuDrawerListElementIcon icon="PACKAGE_VARIANT_CLOSED" />
              <SubMenuDrawerListElementLabel>
                Delivery Tiers
              </SubMenuDrawerListElementLabel>
            </SubMenuDrawerListElement>

            {unbundlingRelease2aEnabled && !unbundlingEnabled && (
              <SubMenuDrawerListElement
                route={ROUTES.ORDER_RULES}
                onClick={() => navigate(ROUTES.ORDER_RULES)}
              >
                <SubMenuDrawerListElementIcon icon="CLIPBOARD_CHECK_OUTLINE" />
                <SubMenuDrawerListElementLabel>
                  Order Rules
                </SubMenuDrawerListElementLabel>
              </SubMenuDrawerListElement>
            )}
          </MenuDisclosurePanel>
        </MenuDisclosure>
      )}

      <MenuDisclosure
        index={6}
        routes={[
          ROUTES.LAYOUT_GROUPING,
          ROUTES.SUPPLIERS,
          ROUTES.STOREFRONT_CONTENT,
          ROUTES.STOREFRONT_SETTINGS,
          ROUTES.STOREFRONT_SHARE,
        ]}
      >
        <MenuDisclosureButton onClick={() => navigate(ROUTES.LAYOUT_GROUPING)}>
          <MenuDrawerListElementIcon icon="STOREFRONT_OUTLINE" />
          <MenuDrawerListElementLabel>Storefront</MenuDrawerListElementLabel>
        </MenuDisclosureButton>
        <MenuDisclosurePanel>
          <SubMenuDrawerListElement
            route={ROUTES.LAYOUT_GROUPING}
            onClick={() => navigate(ROUTES.LAYOUT_GROUPING)}
          >
            <SubMenuDrawerListElementIcon icon="VIEW_DASHBOARD_OUTLINE" />
            <SubMenuDrawerListElementLabel>
              Layout & Grouping
            </SubMenuDrawerListElementLabel>
          </SubMenuDrawerListElement>
          <SubMenuDrawerListElement
            route={ROUTES.STOREFRONT_SETTINGS}
            onClick={() => navigate(ROUTES.STOREFRONT_SETTINGS)}
          >
            <SubMenuDrawerListElementIcon icon="COG_OUTLINE" />
            <SubMenuDrawerListElementLabel>
              Configure
            </SubMenuDrawerListElementLabel>
          </SubMenuDrawerListElement>
          <SubMenuDrawerListElement
            route={ROUTES.SUPPLIERS}
            onClick={() =>
              navigate(
                ROUTES.ORDER_BUYER.href({
                  supplierIdOrUrlId: supplierUrlId,
                }),
              )
            }
          >
            <SubMenuDrawerListElementIcon icon="EYE_OUTLINE" />
            <SubMenuDrawerListElementLabel>
              Preview
            </SubMenuDrawerListElementLabel>
          </SubMenuDrawerListElement>
        </MenuDisclosurePanel>
      </MenuDisclosure>
    </>
  );
};

const CompleteAccountSetup = () => {
  const navigate = useNavigate();
  return (
    <MenuDrawerListElement onClick={() => navigate(ROUTES.SIGNUP)}>
      <MenuDrawerListElementIcon icon="PROFILE" />
      <MenuDrawerListElementLabel>
        Complete account setup
      </MenuDrawerListElementLabel>
    </MenuDrawerListElement>
  );
};

const CompleteOnboarding = () => {
  const navigate = useNavigate();

  return (
    <MenuDrawerListElement onClick={() => navigate(ROUTES.EDIT_PROFILE)}>
      <MenuDrawerListElementIcon icon="CHECK_CIRCLE" />
      <MenuDrawerListElementLabel>Complete account</MenuDrawerListElementLabel>
    </MenuDrawerListElement>
  );
};
type DraftBasketProps = { nbDraftOrders: number };
const DraftBasket = ({ nbDraftOrders }: DraftBasketProps) => {
  const navigate = useNavigate();
  return (
    <MenuDrawerListElement onClick={() => navigate(ROUTES.DRAFT_ORDERS)}>
      <MenuDrawerListElementIcon icon="CART_OUTLINE" />
      <MenuDrawerListElementLabel>
        {nbDraftOrders} draft {pluralize("basket", nbDraftOrders)}
      </MenuDrawerListElementLabel>
    </MenuDrawerListElement>
  );
};

const GetHelp = () => {
  const intercom = useIntercom();
  const [{ unreadMessageCount }] = useSellarContext();
  const { isRailDisplayType } = useMenuDrawerContext();

  return (
    <MenuDrawerListElement onClick={() => intercom.show()}>
      <MenuDrawerListElementIcon
        icon="HELP_CIRCLE_OUTLINE"
        count={
          isRailDisplayType
            ? {
                value: unreadMessageCount,
                className: "bg-red-500 text-white",
              }
            : undefined
        }
      />
      <MenuDrawerListElementLabel>Chat with support</MenuDrawerListElementLabel>
      {unreadMessageCount > 0 && !isRailDisplayType && (
        <span className="flex min-h-4 min-w-4 items-center justify-center rounded-full bg-red-500 p-0.5 text-sm font-semibold text-white">
          {unreadMessageCount}
        </span>
      )}
    </MenuDrawerListElement>
  );
};

type SidebarFooterProps = {
  user: {
    displayName: string;
    email: string;
  };
  userProfileImage: string;
};
const SidebarFooter = ({ user, userProfileImage }: SidebarFooterProps) => {
  const navigate = useNavigate();
  const { isRailDisplayType, setIsOnScreen } = useMenuDrawerContext();

  if (isRailDisplayType) {
    return (
      <div className="border-t border-zinc-300 p-2">
        <Button
          onClick={() => navigate(ROUTES.ACCOUNT_SETTINGS)}
          size="small"
          color="transparent"
        >
          <ProfilePicture size="small" logoUrl={userProfileImage} />
        </Button>
      </div>
    );
  }

  return (
    <div className="border-t border-zinc-300 p-2">
      <Button
        size="small"
        color="transparent"
        className="flex w-full items-center justify-between"
        onClick={() => {
          setIsOnScreen(false);
          navigate(ROUTES.ACCOUNT_SETTINGS);
        }}
      >
        <div className="flex items-center gap-2 overflow-hidden">
          <ProfilePicture size="medium" logoUrl={userProfileImage} />
          <div className="flex flex-col overflow-hidden font-sans text-zinc-700">
            <span className="truncate text-start text-sm font-medium normal-case">
              {user.displayName}
            </span>
            <span className="truncate text-start text-xs font-normal normal-case">
              {user.email}
            </span>
          </div>
        </div>
        <Icon icon="CHEVRON_RIGHT" />
      </Button>
    </div>
  );
};

const BannerCheckBoxItem = ({ message }: { message: string }) => {
  return (
    <div className="flex items-center gap-1 transition-all duration-300 ease-in-out">
      <Icon
        size={0.8}
        icon="CHECKBOX_MARKED_CIRCLE"
        color="#B557F1"
        className="shrink-0"
      />
      <span className="text-sm">{message}</span>
    </div>
  );
};

type FeatureBannerProps = {
  title: string;
  subtitles: string[];
  buttonText: string;
  linkUrl?: string;
  onClick?: () => void;
  classes: string;
  borderColor: string;
};

const FeatureBanner = ({
  title,
  subtitles,
  buttonText,
  linkUrl,
  onClick,
  classes,
  borderColor,
}: FeatureBannerProps) => {
  const { isMobileDisplayType } = useMenuDrawerContext();
  const [isOpen, setIsOpen] = useState(!isMobileDisplayType);
  const iconType: IconType = isOpen ? "CHEVRON_UP" : "CHEVRON_DOWN";
  const { isRailDisplayType, setDisplayType } = useMenuDrawerContext();

  if (isRailDisplayType) {
    return (
      <div className="flex justify-center">
        <Button
          onClick={() => {
            setDisplayType("sticky");
            setIsOpen(true);
          }}
          size="small"
          color="transparent"
          className={twMerge(
            "bg-linear-to-rfrom-0% rounded-full via-100% to-100% p-1",
            classes,
          )}
        >
          <Icon size={0.8} icon="LIGHTNING_BOLT" color="white" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col font-sans text-zinc-800">
      <div
        style={{
          backgroundImage: `url(${preordersBackgroundFillSVG})`,
        }}
        className={twMerge(
          "flex flex-col gap-2 rounded-t-lg border bg-repeat p-3",
          borderColor,
        )}
      >
        <div className="flex items-start justify-between gap-1">
          <span className="flex-1 font-semibold">{title}</span>
          <Icon
            size={1}
            className="min-w-6 transition duration-200 ease-out hover:cursor-pointer"
            icon={iconType}
            onClick={() => setIsOpen((prevIsOpen) => !prevIsOpen)}
          />
        </div>
        {isOpen && (
          <div className="flex flex-col gap-1">
            {subtitles.map((subtitle) => (
              <BannerCheckBoxItem key={subtitle} message={subtitle} />
            ))}
          </div>
        )}
      </div>
      {!!linkUrl && (
        <a
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={twMerge(
            classes,
            "flex w-full justify-center rounded-b-lg bg-linear-to-br from-0% via-100% to-100% p-3 text-sm font-semibold text-white uppercase hover:from-[#8d24cc] hover:to-[#4d36cc]",
          )}
        >
          {buttonText}
        </a>
      )}
      {!!onClick && (
        <Button
          onClick={onClick}
          size="small"
          className={twMerge(
            classes,
            "flex w-full justify-center rounded-t-none rounded-b-lg bg-linear-to-br from-0% via-100% to-100% p-3 text-sm font-semibold text-white uppercase hover:from-[#8d24cc] hover:to-[#4d36cc]",
          )}
        >
          {buttonText}
        </Button>
      )}
    </div>
  );
};

export const RequestCreditAccessDialog = ({
  active,
  company,
  onDismiss,
}: {
  active: boolean;
  company: string;
  onDismiss: () => void;
}) => {
  const { business } = useSellarCurrentBusiness();
  const [message, setMessage] = useState(
    "Hi there, just a reminder for you to switch on Sellar Credit for me",
  );
  const [error, setError] = useState<string>();
  const requestCreditAccessMutation = useBusinessUserCreditAccess(
    business?.id ?? 0,
  );

  const onSubmit = () => {
    requestCreditAccessMutation.mutate(
      {
        message,
      },
      {
        onSuccess: () => {
          onDismiss();
        },
        onError: (error) => {
          setError(
            apiV2AssertError(error)?.response?.data.message ?? "Unknown error",
          );
        },
      },
    );
  };
  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title="Request access to Sellar Credit"
        description={`${company}'s Company Director is responsible for who gets access to Sellar Credit. You can send them a message to request access.`}
      />
      <DialogContent>
        {!!error && (
          <Alert
            type="error"
            title="An error occured"
            message={error}
            className="mb-4"
          />
        )}
        <div className="flex flex-col gap-2">
          <InputLabel>*Your message</InputLabel>
          <InputContainer>
            <InputText
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </InputContainer>
        </div>
      </DialogContent>
      <DialogCTA>
        <Button
          onClick={onSubmit}
          loading={requestCreditAccessMutation.isPending}
        >
          Request access
        </Button>
      </DialogCTA>
    </Dialog>
  );
};

const CreditBalanceToolTip = ({
  active,
  onCloseTooltip,
}: {
  active: boolean;
  onCloseTooltip: () => void;
}) => {
  const { isOnScreen, isMobileDisplayType } = useMenuDrawerContext();

  if (!active) {
    return null;
  }

  // even if active, don't show on mobile unless drawer is open
  if (!isOnScreen && isMobileDisplayType) {
    return null;
  }

  return (
    <TooltipDismiss>
      <TooltipDismissContent
        title={"NEW! Sellar Credit balance"}
        description={
          "Your Sellar Credit balance is always within reach. Clicking on it will bring you to your Company area, where you can manage everything related to your Company and Sellar Credit."
        }
        onClick={onCloseTooltip}
      />
    </TooltipDismiss>
  );
};

export const CreditBalance = ({
  creditAmountAvailable,
  creditPermissions: { canView, canAccess },
  showSellarCreditBalanceTooltip,
  className,
  isSellarCreditBalanceLow,
  onClickBalance,
  onCloseTooltip,
  noLongerEligibleForSellarCredit,
}: {
  creditAmountAvailable: number | null;
  creditPermissions: { canView: boolean; canAccess: boolean };
  showSellarCreditBalanceTooltip: boolean;
  className?: string;
  isSellarCreditBalanceLow: boolean;
  onClickBalance?: () => void;
  onCloseTooltip: () => void;
  noLongerEligibleForSellarCredit?: boolean;
}) => {
  if (canAccess && !canView) {
    return null;
  }

  return (
    <>
      <div
        className={twMerge("border-0 border-t border-zinc-300", className)}
      />
      <div className={twMerge("p-2", onClickBalance && "cursor-pointer")}>
        <div
          className="flex w-full items-center justify-between gap-1 rounded-lg bg-zinc-100 px-1 py-[6px] xl:mx-0 xl:w-auto xl:grow"
          onClick={onClickBalance}
        >
          <div className="flex w-18 items-center gap-2">
            <SellarIconGradient />
            {noLongerEligibleForSellarCredit ? (
              <span className="text-sm font-semibold text-nowrap text-[#D213A6]">
                No credit
              </span>
            ) : (
              <span className="text-sm font-semibold text-[#D213A6]">
                Available
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {!canAccess && <Icon icon="LOCK" size={0.7} />}
            {canView && (
              <div
                className={twMerge(
                  "flex items-center",
                  isSellarCreditBalanceLow && "text-orange-800",
                )}
              >
                <span className="text-sm font-semibold">
                  {creditAmountAvailable === null
                    ? "-"
                    : formatCurrency(creditAmountAvailable)}
                </span>
                <Icon icon="CHEVRON_RIGHT" />
              </div>
            )}
          </div>
        </div>
      </div>

      <CreditBalanceToolTip
        active={showSellarCreditBalanceTooltip}
        onCloseTooltip={onCloseTooltip}
      />
    </>
  );
};

const CreditEligible = ({
  creditLimitQuoteAmount,
  onClick,
}: {
  creditLimitQuoteAmount: number;
  onClick: () => void;
}) => {
  return (
    <>
      <div className="border-0 border-t border-zinc-300" />
      <div className={twMerge("p-2")}>
        <div
          className="flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-zinc-300 bg-zinc-100 px-2 py-[6px] xl:mx-0 xl:w-auto xl:grow"
          onClick={onClick}
        >
          <Icon icon="LOCK" size={0.7} />
          <span className="text-sm font-semibold">
            {formatCurrency(creditLimitQuoteAmount)}
          </span>
        </div>
      </div>
    </>
  );
};

const TwoCreditUnavilable = ({ onClick }: { onClick: () => void }) => {
  return (
    <>
      <div className={twMerge("border-0 border-t border-zinc-300")} />
      <div className={"cursor-pointer p-2"}>
        <div
          className="flex w-full items-center justify-between gap-1 rounded-lg bg-zinc-100 px-1 py-[6px] xl:mx-0 xl:w-auto xl:grow"
          onClick={onClick}
        >
          <div className="flex items-center gap-2">
            <SellarIconGradient />
            <span className="text-sm font-semibold text-[#D213A6]">
              Sellar Credit{" "}
              <span className="text-sm font-semibold text-zinc-800">tab</span>
            </span>
          </div>
        </div>
      </div>
    </>
  );
};
